/**
 * CheckoutForm Component
 * 
 * Reusable checkout form extracted from PaymentCheckoutModal.
 * Works both inline (on PlanDetail page) and inside a modal.
 * 
 * Handles:
 * - User detail form (pre-filled for authenticated users)
 * - Coupon validation & auto-apply from URL params
 * - Charges calculation
 * - Razorpay payment integration
 * - Guest vs authenticated user flows
 * - Free subscription flow (100% coupon discount)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Loader2, 
  Tag, 
  Check, 
  AlertCircle,
  CreditCard,
  User,
  Mail,
  Phone,
  Calendar,
  FileText,
  X
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useRazorpay } from '../hooks/useRazorpay';
import { paymentAPI, profileAPI, getErrorMessage } from '../lib/api';
import { toast } from '../hooks/use-toast';

/**
 * Format currency in Indian Rupees
 */
const formatCurrency = (amount) => {
  return `₹${Number(amount).toLocaleString('en-IN', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
};

/**
 * Validate PAN card format (AAAAA0000A)
 */
const validatePAN = (pan) => {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan.toUpperCase());
};

/**
 * Validate email format
 */
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (10 digits)
 */
const validatePhone = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10;
};

const CheckoutForm = ({ 
  plan,
  onSuccess,
  initialCouponCode = '',
  couponTargetPlanId = null,
  /** 'inline' for PlanDetail page, 'modal' for PaymentCheckoutModal */
  variant = 'inline',
  /** Called when close button is clicked (modal variant only) */
  onClose,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { openCheckout, isLoading: razorpayLoading } = useRazorpay();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    dob: '',
    pancard: '',
    couponCode: '',
  });
  
  // UI state
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // Coupon & charges state
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [charges, setCharges] = useState(null);
  
  // Track if initial coupon has been applied
  const [initialCouponApplied, setInitialCouponApplied] = useState(false);

  // Load user profile for authenticated users
  useEffect(() => {
    const loadProfile = async () => {
      if (isAuthenticated && plan) {
        setIsLoadingProfile(true);
        try {
          const response = await profileAPI.getProfile();
          const profile = response.data?.data || response.data;
          
          setFormData(prev => ({
            ...prev,
            name: profile.name || user?.name || '',
            email: profile.email || user?.email || '',
            whatsapp: profile.whatsapp || '',
            dob: profile.dob || '',
            pancard: profile.pancard || '',
          }));
        } catch (err) {
          console.error('Failed to load profile:', err);
          setFormData(prev => ({
            ...prev,
            name: user?.name || '',
            email: user?.email || '',
          }));
        } finally {
          setIsLoadingProfile(false);
        }
      }
    };
    
    loadProfile();
  }, [isAuthenticated, plan, user]);

  // Calculate charges when plan or coupon changes
  useEffect(() => {
    const calculateCharges = async () => {
      if (!plan?.id) return;
      
      setIsCalculating(true);
      try {
        const response = await paymentAPI.calculateCharges(
          plan.id, 
          appliedCoupon?.coupon_code || ''
        );
        const data = response.data?.data || response.data;
        setCharges(data);
      } catch (err) {
        console.error('Failed to calculate charges:', err);
        setCharges({
          plan_original_price: plan.after_discount,
          base_amount: plan.after_discount,
          gateway_charges: 0,
          gst_amount: 0,
          total_amount: plan.after_discount,
          coupon: null,
        });
      } finally {
        setIsCalculating(false);
      }
    };
    
    calculateCharges();
  }, [plan, appliedCoupon]);

  // Auto-apply initial coupon code (from spinner reward or shared link)
  useEffect(() => {
    const applyInitialCoupon = async () => {
      if (initialCouponCode && plan?.id && !appliedCoupon) {
        // If couponTargetPlanId specified, only auto-apply if plan ID matches
        if (couponTargetPlanId) {
          const planId = Number(plan.id);
          const targetPlanId = Number(couponTargetPlanId);
          if (planId !== targetPlanId) return;
        }
        
        if (initialCouponApplied) return;
        setInitialCouponApplied(true);
        
        setFormData(prev => ({ ...prev, couponCode: initialCouponCode }));
        setIsCouponLoading(true);
        setCouponError('');
        
        try {
          const response = await paymentAPI.checkCoupon(initialCouponCode, plan.id);
          const data = response.data?.data || response.data;
          
          setAppliedCoupon(data);
          
          const discountText = data.discount_type === 'first_month_free' 
            ? 'First month free discount'
            : `${data.percentage}% discount`;
          
          toast({
            title: 'Coupon Auto-Applied!',
            description: `Your ${discountText} coupon has been applied`,
            variant: 'success',
          });
        } catch (err) {
          const message = getErrorMessage(err);
          setCouponError(message);
          setAppliedCoupon(null);
        } finally {
          setIsCouponLoading(false);
        }
      }
    };
    
    applyInitialCoupon();
  }, [initialCouponCode, plan?.id, initialCouponApplied, appliedCoupon, couponTargetPlanId]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.whatsapp.trim()) {
      newErrors.whatsapp = 'WhatsApp number is required';
    } else if (!validatePhone(formData.whatsapp)) {
      newErrors.whatsapp = 'Enter valid 10-digit mobile number';
    }
    if (!formData.dob) newErrors.dob = 'Date of birth is required';
    if (!formData.pancard.trim()) {
      newErrors.pancard = 'PAN card is required';
    } else if (!validatePAN(formData.pancard)) {
      newErrors.pancard = 'Invalid PAN format (e.g., ABCDE1234F)';
    }
    if (!termsAccepted) newErrors.terms = 'Please accept the terms and conditions';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Apply coupon
  const handleApplyCoupon = async () => {
    if (!formData.couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    setIsCouponLoading(true);
    setCouponError('');
    
    try {
      const response = await paymentAPI.checkCoupon(formData.couponCode.trim(), plan.id);
      const data = response.data?.data || response.data;
      
      setAppliedCoupon(data);
      toast({
        title: 'Coupon Applied!',
        description: `${data.percentage}% discount applied`,
        variant: 'success',
      });
    } catch (err) {
      const message = getErrorMessage(err);
      setCouponError(message || 'Failed to apply coupon. Please try again.');
      setAppliedCoupon(null);
    } finally {
      setIsCouponLoading(false);
    }
  };

  // Remove coupon
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setFormData(prev => ({ ...prev, couponCode: '' }));
    setCouponError('');
  };

  // Handle free subscription (zero-amount when coupon covers full price)
  const handleFreeSubscription = async () => {
    try {
      const subscriptionData = {
        plan_id: plan.id,
        coupon_code: appliedCoupon?.coupon_code || '',
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        dob: formData.dob,
        whatsapp: formData.whatsapp.replace(/\D/g, ''),
        pancard: formData.pancard.trim().toUpperCase(),
      };
      
      let response;
      if (isAuthenticated) {
        response = await paymentAPI.activateFreeSubscription(subscriptionData);
      } else {
        response = await paymentAPI.guestActivateFreeSubscription(subscriptionData);
      }
      
      const result = response.data?.data || response.data;
      
      toast({
        title: 'Subscription Activated!',
        description: result.message || 'Your free subscription has been activated successfully.',
        variant: 'success',
      });
      
      if (onClose) onClose();
      
      navigate('/payment/success', { 
        state: { 
          subscription: result.subscription,
          isGuest: !isAuthenticated,
          planName: plan.plan_name,
          planCategory: plan.plan_category,
          isFreeSubscription: true,
          account: result.account,
        },
        replace: true,
      });
      
      if (onSuccess) onSuccess(result.subscription);
      
    } catch (err) {
      console.error('Free subscription error:', err);
      
      const errorCode = err.response?.data?.error?.code || err.response?.data?.code;
      if (errorCode === 'USER_EXISTS') {
        setShowLoginPrompt(true);
        return;
      }
      
      const message = getErrorMessage(err);
      toast({
        title: 'Subscription Failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle payment
  const handlePayment = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const isFreeSubscription = charges && charges.total_amount <= 0.01;
      
      if (isFreeSubscription) {
        await handleFreeSubscription();
        return;
      }
      
      const orderData = {
        plan_id: plan.id,
        coupon_code: appliedCoupon?.coupon_code || '',
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        dob: formData.dob,
        whatsapp: formData.whatsapp.replace(/\D/g, ''),
        pancard: formData.pancard.trim().toUpperCase(),
      };
      
      let orderResponse;
      if (isAuthenticated) {
        orderResponse = await paymentAPI.createOrder(orderData);
      } else {
        orderResponse = await paymentAPI.guestCreateOrder(orderData);
      }
      
      const orderResult = orderResponse.data?.data || orderResponse.data;
      
      const paymentResult = await openCheckout({
        key: orderResult.razorpay_key,
        amount: orderResult.amount_in_paise,
        currency: orderResult.currency || 'INR',
        name: 'Climb Capital Services',
        description: `${plan.plan_name} - ${plan.duration_months} Months`,
        order_id: orderResult.order_id,
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.whatsapp,
        },
        theme: { color: '#1b2d4a' },
        notes: {
          plan_id: plan.id,
          plan_name: plan.plan_name,
        },
      });
      
      const verifyData = {
        razorpay_payment_id: paymentResult.razorpay_payment_id,
        razorpay_order_id: paymentResult.razorpay_order_id,
        razorpay_signature: paymentResult.razorpay_signature,
        plan_id: plan.id,
        amount: orderResult.amount,
        coupon_code: appliedCoupon?.coupon_code || '',
        base_amount: charges?.base_amount,
      };
      
      let verifyResponse;
      if (isAuthenticated) {
        verifyData.name = formData.name;
        verifyData.email = formData.email;
        verifyData.dob = formData.dob;
        verifyData.whatsapp = formData.whatsapp;
        verifyData.pancard = formData.pancard.toUpperCase();
        verifyResponse = await paymentAPI.verifyPayment(verifyData);
      } else {
        verifyData.user_id = orderResult.user_id;
        verifyData.temp_password = orderResult.temp_password;
        verifyResponse = await paymentAPI.guestVerifyPayment(verifyData);
      }
      
      const result = verifyResponse.data?.data || verifyResponse.data;
      
      if (onClose) onClose();
      
      navigate('/payment/success', { 
        state: { 
          subscription: result,
          isGuest: !isAuthenticated,
          planName: plan.plan_name,
          planCategory: plan.plan_category,
        },
        replace: true,
      });
      
      if (onSuccess) onSuccess(result);
      
    } catch (err) {
      console.error('Payment error:', err);
      
      if (err.dismissed) {
        toast({
          title: 'Payment Cancelled',
          description: 'You cancelled the payment. You can try again.',
          variant: 'default',
        });
        return;
      }
      
      const errorCode = err.response?.data?.error?.code || err.response?.data?.code;
      if (errorCode === 'USER_EXISTS') {
        setShowLoginPrompt(true);
        return;
      }
      
      const message = err.description || err.reason || getErrorMessage(err);
      
      if (err.code || err.razorpay_payment_id === undefined) {
        if (onClose) onClose();
        navigate('/payment/failure', {
          state: {
            error: message,
            planId: plan.id,
            planName: plan.plan_name,
          },
          replace: true,
        });
      } else {
        toast({
          title: 'Payment Failed',
          description: message,
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Render ----

  const renderFormFields = () => (
    <>
      {/* Loading Profile */}
      {isLoadingProfile && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="animate-spin mr-2 text-blue-600" size={20} />
          <span className="text-gray-600">Loading your details...</span>
        </div>
      )}
      
      {!isLoadingProfile && (
        <>
          {/* Full Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
            </div>
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>
          
          {/* Email Address */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading || (isAuthenticated && formData.email)}
              />
            </div>
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>
          
          {/* WhatsApp Number */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">WhatsApp Number</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="tel"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleInputChange}
                placeholder="Enter your WhatsApp number"
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.whatsapp ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
            </div>
            {errors.whatsapp && <p className="text-red-500 text-xs mt-1">{errors.whatsapp}</p>}
          </div>
          
          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Date of Birth</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.dob ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
            </div>
            {errors.dob && <p className="text-red-500 text-xs mt-1">{errors.dob}</p>}
          </div>
          
          {/* PAN Card Number */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">PAN Card Number</label>
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                name="pancard"
                value={formData.pancard}
                onChange={handleInputChange}
                placeholder="Enter your PAN card number"
                maxLength={10}
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase ${
                  errors.pancard ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
            </div>
            <p className="text-gray-500 text-xs mt-1">Please enter your 10-digit PAN card number.</p>
            {errors.pancard && <p className="text-red-500 text-xs mt-1">{errors.pancard}</p>}
          </div>
          
          {/* Coupon Code */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Coupon Code</label>
            {appliedCoupon ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <Tag className="text-green-600" size={18} />
                  <span className="font-medium text-green-700">
                    {appliedCoupon.coupon_code} ({appliedCoupon.discount_type === 'first_month_free' ? 'First Month Free' : `${appliedCoupon.percentage}% off`})
                  </span>
                </div>
                <button
                  onClick={handleRemoveCoupon}
                  className="text-red-500 hover:text-red-700 text-sm font-medium"
                  disabled={isLoading}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    name="couponCode"
                    value={formData.couponCode}
                    onChange={handleInputChange}
                    placeholder="Enter coupon code (optional)"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoading || isCouponLoading}
                  />
                </div>
                <button
                  onClick={handleApplyCoupon}
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading || isCouponLoading || !formData.couponCode.trim()}
                >
                  {isCouponLoading ? <Loader2 className="animate-spin" size={20} /> : 'Apply Coupon'}
                </button>
              </div>
            )}
            {couponError && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle size={12} />
                {couponError}
              </p>
            )}
          </div>
          
          {/* Charges Breakdown */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-gray-900 mb-3">Charges Breakdown:</h3>
            {isCalculating ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="animate-spin mr-2 text-blue-600" size={20} />
                <span className="text-gray-600">Calculating...</span>
              </div>
            ) : charges ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Base Amount:</span>
                  <span className="font-medium">{formatCurrency(charges.plan_original_price)}</span>
                </div>
                {charges.coupon && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount ({charges.coupon.discount_type === 'first_month_free' ? 'First Month Free' : `${charges.coupon.percentage}%`}):</span>
                    <span className="font-medium">-{formatCurrency(charges.coupon.discount_amount)}</span>
                  </div>
                )}
                {charges.gateway_charges > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Payment Gateway Charges:</span>
                    <span className="font-medium">{formatCurrency(charges.gateway_charges)}</span>
                  </div>
                )}
                {charges.gst_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">GST (18%):</span>
                    <span className="font-medium">{formatCurrency(charges.gst_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">{formatCurrency(charges.base_amount)}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-bold text-gray-900">Total Amount:</span>
                    <span className="font-bold text-lg text-gray-900">{formatCurrency(charges.total_amount)}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-4 text-gray-500">
                Please select a plan to see the price.
              </div>
            )}
          </div>
          
          {/* Terms and Conditions */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="checkout-terms"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                if (errors.terms) setErrors(prev => ({ ...prev, terms: '' }));
              }}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              disabled={isLoading}
            />
            <label htmlFor="checkout-terms" className="text-sm text-gray-600">
              I accept the{' '}
              <button
                type="button"
                onClick={() => setShowTermsModal(true)}
                className="text-blue-600 hover:underline font-medium"
              >
                Terms and Conditions
              </button>
            </label>
          </div>
          {errors.terms && <p className="text-red-500 text-xs -mt-3">{errors.terms}</p>}
        </>
      )}
    </>
  );

  const renderPayButton = () => (
    <button
      onClick={handlePayment}
      className={`w-full px-6 py-3 text-white rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
        charges && charges.total_amount <= 0.01 
          ? 'bg-green-600 hover:bg-green-700' 
          : 'bg-blue-600 hover:bg-blue-700'
      }`}
      disabled={isLoading || isLoadingProfile || razorpayLoading || isCalculating}
    >
      {isLoading ? (
        <>
          <Loader2 className="animate-spin" size={20} />
          Processing...
        </>
      ) : charges && charges.total_amount <= 0.01 ? (
        <>
          <Check size={20} />
          Subscribe Now
        </>
      ) : (
        <>
          <CreditCard size={20} />
          Proceed to Payment
        </>
      )}
    </button>
  );

  const renderTermsModal = () => (
    showTermsModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-xl font-bold text-gray-900">Terms and Conditions</h3>
            <button
              onClick={() => setShowTermsModal(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="p-6 overflow-y-auto">
            <p className="text-gray-700 leading-relaxed">
              I fully understand and remain committed to complying with all SEBI regulations regarding quarterly payments for availing services. To ensure uninterrupted access to Climb Capital Services Private Limited's Stock Recommendation Service, which plays a vital role in my operations and decision-making, I have voluntarily decided to make an advance payment. This proactive approach is intended to eliminate any possibility of delays or service interruptions, enabling a smooth and continuous experience. By choosing to make this advance payment, I aim to secure uninterrupted access to their valuable services while ensuring that all regulatory requirements under SEBI guidelines are properly fulfilled.
            </p>
          </div>
          <div className="p-6 border-t bg-gray-50 rounded-b-2xl">
            <button
              onClick={() => setShowTermsModal(false)}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors"
            >
              I Agree
            </button>
          </div>
        </div>
      </div>
    )
  );

  const renderLoginPrompt = () => (
    showLoginPrompt && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User size={32} className="text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Account Already Exists</h3>
            <p className="text-gray-600 mb-6">
              An account with email <strong>{formData.email}</strong> already exists. 
              Please login to continue with your purchase.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  sessionStorage.setItem('pendingPurchase', JSON.stringify({
                    planId: plan.id,
                    couponCode: appliedCoupon?.coupon_code || initialCouponCode || '',
                    couponTargetPlanId: couponTargetPlanId,
                  }));
                  navigate(`/login?redirect=/plans/${plan.id}`);
                }}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <User size={20} />
                Login to Continue
              </button>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-full font-semibold hover:bg-gray-200 transition-colors"
              >
                Use Different Email
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );

  return (
    <>
      <div className="space-y-5">
        {renderFormFields()}
        <div className="pt-2">
          {renderPayButton()}
        </div>
      </div>
      {renderTermsModal()}
      {renderLoginPrompt()}
    </>
  );
};

export default CheckoutForm;
